import type { Category } from '../types';
import { FACTS } from '../facts';

const FEE = `$${FACTS.deliveryFeeUsd}`;
const LANE = `$${FACTS.lanePassPerDeliveryUsd}`;
const LANE_MAX = `$${FACTS.lanePassWeeklyMaxUsd}`;
const CONFIRM_H = `${FACTS.customerConfirmHours}`;
const SET_ASIDE = `${FACTS.taxSetAsidePct.low}–${FACTS.taxSetAsidePct.high}%`;

export const taxes101: Category = {
  slug: 'taxes-101',
  half: 'business',
  accent: '#E08A00',
  icon: 'dollar',
  label: { en: "Taxes 101 for Runners", es: "Impuestos 101 para Runners" },
  blurb: {
    en: "You're an independent contractor. Quarterly estimated taxes, self-employment tax, and what your W-9 means for the 1099-NEC.",
    es: "Eres un contratista independiente. Impuestos estimados trimestrales, impuesto sobre el trabajo por cuenta propia y qué significa tu W-9 para el 1099-NEC.",
  },
  guides: [
    {
      slug: 'w9-and-1099',
      irsLinks: ['w-9', '1099-nec'],
      related: ['set-up-your-business/sole-prop-vs-llc', 'taxes-101/what-counts-as-income'],
      body: {
        en: {
          title: "Your W-9 and the 1099-NEC",
          summary: "Every Runner gives Grocery List Plus a W-9 before go-live; whether a 1099-NEC follows depends on the box you check.",
          sections: [
            {
              heading: "The W-9 comes first",
              paragraphs: [
                "Before your first delivery, Grocery List Plus asks for a Form W-9. It gives us your legal name, business name if any, taxpayer number (SSN or EIN), and how your business is classified for tax purposes. We keep it on file; it doesn't go to the IRS.",
                "Fill it in carefully. The classification box drives everything that follows.",
              ],
            },
            {
              heading: "Who gets a 1099-NEC",
              paragraphs: [
                "A 1099-NEC is the form a business sends a contractor showing what it paid them that year, once payments go above the IRS reporting threshold. Whether you get one depends on your W-9:",
              ],
              bullets: [
                "Sole proprietor or individual: generally yes.",
                "Single-member LLC taxed as a sole proprietorship: generally yes.",
                "LLC taxed as a partnership: generally yes.",
                "LLC taxed as an S-corp or C-corp: generally no.",
                "So 'I'm a 1099 contractor' isn't the whole story. You're an independent contractor; the 1099 is one way that gets reported.",
              ],
            },
            {
              heading: "What the number includes",
              paragraphs: [
                "The 1099-NEC reflects what Grocery List Plus paid you through the platform — delivery fees and tips. Your pay statements show the lane pass as its own line; whether it is netted out of the reported total or deducted by you as an expense is worth confirming with an accountant when you file, so it is neither missed nor double-counted.",
              ],
            },
            {
              heading: "No 1099? You still owe tax",
              paragraphs: [
                "If your entity type means no 1099-NEC, or your total stayed under the threshold, the income is still taxable. Report it from your pay statements. Confirm with an accountant if your classification changed mid-year.",
              ],
            },
          ],
          actions: [
            "Check that the classification box on the W-9 you gave Grocery List Plus still matches how you're set up.",
            "Download your year-to-date pay statements and total the gross.",
            "If you formed an LLC or changed classification this year, contact support so we can collect an updated W-9.",
          ],
        },
        es: {
          title: "Tu W-9 y el 1099-NEC",
          summary: "Cada Runner le entrega un W-9 a Lista de Compras Plus antes de empezar; que después llegue un 1099-NEC depende de la casilla que marques.",
          sections: [
            {
              heading: "El W-9 va primero",
              paragraphs: [
                "Antes de tu primera entrega, Lista de Compras Plus te pide un Formulario W-9. Nos da tu nombre legal, el nombre de tu negocio si lo tienes, tu número de contribuyente (SSN o EIN) y cómo está clasificado tu negocio para efectos fiscales. Lo guardamos en archivo; no se envía al IRS.",
                "Llénalo con cuidado. La casilla de clasificación determina todo lo que sigue.",
              ],
            },
            {
              heading: "Quién recibe un 1099-NEC",
              paragraphs: [
                "Un 1099-NEC es el formulario que un negocio le envía a un contratista mostrando cuánto le pagó ese año, una vez que los pagos superan el umbral de reporte del IRS. Que recibas uno depende de tu W-9:",
              ],
              bullets: [
                "Propietario único o persona física: generalmente sí.",
                "LLC de un solo miembro que tributa como propietario único: generalmente sí.",
                "LLC que tributa como sociedad (partnership): generalmente sí.",
                "LLC que tributa como S-corp o C-corp: generalmente no.",
                "Así que 'soy un contratista 1099' no cuenta toda la historia. Eres un contratista independiente; el 1099 es una de las formas en que eso se reporta.",
              ],
            },
            {
              heading: "Qué incluye la cifra",
              paragraphs: [
                "El 1099-NEC refleja lo que Lista de Compras Plus te pagó a través de la plataforma: tarifas de entrega y propinas. Tus estados de pago muestran el lane pass en su propia línea; confirma con un contador si ya viene descontado del total reportado o si lo deduces tú como gasto, para que no se pierda ni se cuente dos veces.",
              ],
            },
            {
              heading: "¿Sin 1099? Igual debes impuestos",
              paragraphs: [
                "Si por tu tipo de entidad no recibes 1099-NEC, o tu total quedó por debajo del umbral, el ingreso sigue siendo gravable. Repórtalo a partir de tus estados de pago. Confírmalo con un contador si tu clasificación cambió a mitad de año.",
              ],
            },
          ],
          actions: [
            "Revisa que la casilla de clasificación del W-9 que le diste a Lista de Compras Plus siga coincidiendo con cómo estás organizado.",
            "Descarga tus estados de pago del año hasta la fecha y suma el monto bruto.",
            "Si formaste una LLC o cambiaste de clasificación este año, avísale a soporte para que recojamos un W-9 actualizado.",
          ],
        },
      },
    },
    {
      slug: 'quarterly-estimated-taxes',
      irsLinks: ['1040-ES'],
      related: ['recordkeeping-and-money/set-aside-for-taxes', 'taxes-101/tax-calendar', 'taxes-101/self-employment-tax'],
      body: {
        en: {
          title: "Quarterly estimated taxes",
          summary: "Nobody withholds tax from your payouts, so the IRS expects you to send it in four times a year with Form 1040-ES.",
          sections: [
            {
              heading: "Why quarterly",
              paragraphs: [
                "When you had a W-2 job, tax came out of every paycheck. As a Runner, your weekly Stripe Connect payout arrives with nothing withheld. The IRS still wants its share as you earn it, not in one lump next April. That's what estimated payments are.",
                "Skip them and you can owe an underpayment penalty on top of the tax, even if you pay in full at filing time.",
              ],
            },
            {
              heading: "The four due dates",
              bullets: [
                "Mid-April, for January through March.",
                "Mid-June, for April and May.",
                "Mid-September, for June through August.",
                "Mid-January of the next year, for September through December.",
                "Exact dates shift when they land on a weekend or holiday. Check Form 1040-ES each year.",
              ],
            },
            {
              heading: "How much to send",
              paragraphs: [
                `The honest answer: it depends on your total income, your household, and your state. A workable starting point is to set aside ${SET_ASIDE} of your delivery profit (income minus expenses) every payout, then send what you've saved each quarter. Your first year, an accountant can help you pick a safer number based on last year's tax.`,
                "Many states want estimated payments too, on their own schedule.",
              ],
            },
            {
              heading: "How to pay",
              paragraphs: [
                "IRS Direct Pay or an IRS online account, straight from your bank. No fee. You can also mail a 1040-ES voucher with a check. Keep the confirmation; you'll enter the total on your return.",
              ],
            },
          ],
          actions: [
            "Put the four due dates in your phone calendar with a reminder one week before.",
            "Open a separate savings account just for the set-aside if you haven't.",
            "Look up whether your state requires estimated payments.",
          ],
        },
        es: {
          title: "Impuestos estimados trimestrales",
          summary: "Nadie retiene impuestos de tus pagos, así que el IRS espera que los envíes cuatro veces al año con el Formulario 1040-ES.",
          sections: [
            {
              heading: "Por qué trimestrales",
              paragraphs: [
                "Cuando tenías un empleo con W-2, el impuesto salía de cada cheque. Como Runner, tu pago semanal por Stripe Connect llega sin ninguna retención. El IRS igual quiere su parte conforme la ganas, no en un solo pago el próximo abril. Para eso son los pagos estimados.",
                "Si te los saltas, puedes deber una multa por pago insuficiente además del impuesto, aunque pagues todo al presentar tu declaración.",
              ],
            },
            {
              heading: "Las cuatro fechas límite",
              bullets: [
                "Mediados de abril, por enero a marzo.",
                "Mediados de junio, por abril y mayo.",
                "Mediados de septiembre, por junio a agosto.",
                "Mediados de enero del año siguiente, por septiembre a diciembre.",
                "Las fechas exactas se mueven cuando caen en fin de semana o día feriado. Revisa el Formulario 1040-ES cada año.",
              ],
            },
            {
              heading: "Cuánto enviar",
              paragraphs: [
                `La respuesta honesta: depende de tu ingreso total, tu hogar y tu estado. Un punto de partida práctico es apartar el ${SET_ASIDE} de tu ganancia por entregas (ingresos menos gastos) en cada pago, y luego enviar lo ahorrado cada trimestre. En tu primer año, un contador puede ayudarte a elegir una cifra más segura basada en el impuesto del año anterior.`,
                "Muchos estados también quieren pagos estimados, con su propio calendario.",
              ],
            },
            {
              heading: "Cómo pagar",
              paragraphs: [
                "IRS Direct Pay o una cuenta en línea del IRS, directo desde tu banco. Sin comisión. También puedes enviar por correo un comprobante del 1040-ES con un cheque. Guarda la confirmación; anotarás el total en tu declaración.",
              ],
            },
          ],
          actions: [
            "Pon las cuatro fechas en el calendario de tu teléfono con un recordatorio una semana antes.",
            "Abre una cuenta de ahorros aparte solo para el apartado, si aún no lo has hecho.",
            "Averigua si tu estado exige pagos estimados.",
          ],
        },
      },
    },
    {
      slug: 'self-employment-tax',
      irsLinks: ['schedule-se', 'schedule-c'],
      related: ['taxes-101/schedule-c-basics', 'deductions/mileage', 'recordkeeping-and-money/set-aside-for-taxes'],
      body: {
        en: {
          title: "Self-employment tax",
          summary: "It's the Social Security and Medicare tax an employer used to split with you; now you pay both halves, on Schedule SE.",
          sections: [
            {
              heading: "What it is",
              paragraphs: [
                "At a W-2 job, Social Security and Medicare taxes were split: the employer paid half, you paid half out of your check. As a Runner there's no employer, so you cover both halves yourself. That's self-employment tax. It's separate from, and on top of, regular income tax.",
                "It's not a penalty for working for yourself. It's the same coverage, funded a different way, and it builds your Social Security record.",
              ],
            },
            {
              heading: "How it's figured",
              bullets: [
                "Schedule C gives you your net profit: delivery fees plus tips, minus expenses like the lane pass and mileage.",
                "Schedule SE takes that profit and computes the tax. The rate and the Social Security wage cap are printed on the form each year.",
                "You get to deduct half of the self-employment tax when figuring your income tax. The form walks you through it.",
              ],
            },
            {
              heading: "Why deductions matter twice",
              paragraphs: [
                "Every dollar of legitimate expense you track lowers your Schedule C profit. That lowers your income tax and your self-employment tax. This is why the mileage log and the receipt habit are worth the two minutes a day.",
              ],
            },
            {
              heading: "Where it shows up in your set-aside",
              paragraphs: [
                `The ${SET_ASIDE} rule of thumb already includes self-employment tax. If you've been setting that aside from profit, you're covering it. If your set-aside was based on income tax alone, revisit it with an accountant.`,
              ],
            },
          ],
          actions: [
            "Open Schedule SE on IRS.gov and read the first page. It's shorter than you'd think.",
            "Check that your set-aside percentage is applied to profit, not just to the fee.",
            "Make sure your expense tracking is running so the number Schedule SE sees is the real one.",
          ],
        },
        es: {
          title: "Impuesto sobre el trabajo por cuenta propia",
          summary: "Es el impuesto de Seguro Social y Medicare que antes un empleador compartía contigo; ahora pagas ambas mitades, en el Anexo SE.",
          sections: [
            {
              heading: "Qué es",
              paragraphs: [
                "En un empleo con W-2, los impuestos de Seguro Social y Medicare se dividían: el empleador pagaba la mitad y tú la otra mitad de tu cheque. Como Runner no hay empleador, así que cubres ambas mitades tú mismo. Eso es el impuesto sobre el trabajo por cuenta propia. Es aparte del impuesto sobre la renta normal, y se suma a él.",
                "No es un castigo por trabajar por tu cuenta. Es la misma cobertura, financiada de otra forma, y construye tu historial en el Seguro Social.",
              ],
            },
            {
              heading: "Cómo se calcula",
              bullets: [
                "El Anexo C te da tu ganancia neta: tarifas de entrega más propinas, menos gastos como el lane pass y el millaje.",
                "El Anexo SE toma esa ganancia y calcula el impuesto. La tasa y el tope salarial del Seguro Social vienen impresos en el formulario cada año.",
                "Puedes deducir la mitad del impuesto sobre el trabajo por cuenta propia al calcular tu impuesto sobre la renta. El formulario te guía paso a paso.",
              ],
            },
            {
              heading: "Por qué las deducciones cuentan dos veces",
              paragraphs: [
                "Cada dólar de gasto legítimo que registras baja tu ganancia del Anexo C. Eso reduce tu impuesto sobre la renta y tu impuesto sobre el trabajo por cuenta propia. Por eso el registro de millaje y el hábito de guardar recibos valen los dos minutos al día.",
              ],
            },
            {
              heading: "Dónde entra en tu apartado",
              paragraphs: [
                `La regla práctica del ${SET_ASIDE} ya incluye el impuesto sobre el trabajo por cuenta propia. Si has estado apartando eso de tu ganancia, lo tienes cubierto. Si tu apartado se basó solo en el impuesto sobre la renta, revísalo con un contador.`,
              ],
            },
          ],
          actions: [
            "Abre el Anexo SE en IRS.gov y lee la primera página. Es más corto de lo que crees.",
            "Verifica que tu porcentaje de apartado se aplique a la ganancia, no solo a la tarifa.",
            "Asegúrate de que tu registro de gastos esté funcionando para que la cifra que ve el Anexo SE sea la real.",
          ],
        },
      },
    },
    {
      slug: 'schedule-c-basics',
      irsLinks: ['schedule-c', 'schedule-se', 'pub-463'],
      related: ['taxes-101/what-counts-as-income', 'deductions/mileage', 'recordkeeping-and-money/receipts-and-bookkeeping'],
      body: {
        en: {
          title: "Schedule C basics",
          summary: "Schedule C is the one-page form where your delivery income and expenses meet and turn into profit.",
          sections: [
            {
              heading: "What it does",
              paragraphs: [
                "Schedule C, Profit or Loss From Business, attaches to your personal Form 1040. Income goes at the top, expenses in the middle, and the difference at the bottom is your net profit. That number flows to your 1040 as income and to Schedule SE for self-employment tax.",
                "Sole proprietors and single-member LLCs taxed as sole proprietorships file it. If your LLC elected S-corp or partnership taxation, you'll file a different return; confirm with an accountant.",
              ],
            },
            {
              heading: "The income line",
              paragraphs: [
                "Your gross receipts are everything Grocery List Plus Dispatch paid you: delivery fees and tips. Pull them from your pay statements and check them against your 1099-NEC; the lane pass shows on the statements as its own line — ask your accountant whether to treat it as an expense or as already netted out, so it is counted once.",
              ],
            },
            {
              heading: "The expense lines Runners use most",
              bullets: [
                "Car and truck expenses: your mileage at the IRS standard rate, or actual costs. Pick one method and keep the log.",
                "Commissions and fees, or other expenses: the lane pass.",
                "Supplies: insulated bags, coolers, phone mount, chargers.",
                "Utilities or phone: the business share of your phone and data plan.",
                "Insurance: a rideshare or delivery endorsement on your auto policy.",
                "Taxes and licenses: a DBA filing, LLC annual fee.",
              ],
            },
            {
              heading: "What isn't on it",
              paragraphs: [
                "Groceries you bought for customers with the Grocery List Plus virtual card. That was never your money, so it's neither income nor expense. Also not on it: transfers to your personal account, and the tax payments themselves.",
              ],
            },
          ],
          actions: [
            "Print a blank Schedule C and pencil in which lines you'd use this year.",
            "Make a folder (paper or digital) for each expense line you ticked.",
            "Confirm with an accountant which return your entity type files.",
          ],
        },
        es: {
          title: "Lo básico del Anexo C",
          summary: "El Anexo C es el formulario de una página donde tus ingresos y gastos por entregas se encuentran y se convierten en ganancia.",
          sections: [
            {
              heading: "Qué hace",
              paragraphs: [
                "El Anexo C, Ganancias o Pérdidas del Negocio, se adjunta a tu Formulario 1040 personal. Los ingresos van arriba, los gastos en medio, y la diferencia al final es tu ganancia neta. Esa cifra pasa a tu 1040 como ingreso y al Anexo SE para el impuesto sobre el trabajo por cuenta propia.",
                "Lo presentan los propietarios únicos y las LLC de un solo miembro que tributan como propietario único. Si tu LLC optó por tributar como S-corp o como sociedad, presentarás una declaración distinta; confírmalo con un contador.",
              ],
            },
            {
              heading: "La línea de ingresos",
              paragraphs: [
                "Tus ingresos brutos son todo lo que Lista de Compras Plus Dispatch te pagó: tarifas de entrega y propinas. Tómalos de tus estados de pago y compáralos con tu 1099-NEC; el lane pass aparece en los estados como su propia línea — pregunta a tu contador si tratarlo como gasto o como ya descontado, para contarlo una sola vez.",
              ],
            },
            {
              heading: "Las líneas de gastos que más usan los Runners",
              bullets: [
                "Gastos de auto y camioneta: tu millaje a la tarifa estándar del IRS, o los costos reales. Elige un método y lleva el registro.",
                "Comisiones y tarifas, u otros gastos: el lane pass.",
                "Suministros: bolsas térmicas, hieleras, soporte para el teléfono, cargadores.",
                "Servicios o teléfono: la parte de negocio de tu plan de teléfono y datos.",
                "Seguro: un endoso de rideshare o de entregas en tu póliza de auto.",
                "Impuestos y licencias: el registro de un DBA, la cuota anual de la LLC.",
              ],
            },
            {
              heading: "Qué no va ahí",
              paragraphs: [
                "La despensa que compraste para tus clientes con la tarjeta virtual de Lista de Compras Plus. Ese dinero nunca fue tuyo, así que no es ingreso ni gasto. Tampoco van: las transferencias a tu cuenta personal ni los pagos de impuestos en sí.",
              ],
            },
          ],
          actions: [
            "Imprime un Anexo C en blanco y marca a lápiz qué líneas usarías este año.",
            "Crea una carpeta (en papel o digital) por cada línea de gasto que marcaste.",
            "Confirma con un contador qué declaración presenta tu tipo de entidad.",
          ],
        },
      },
    },
    {
      slug: 'what-counts-as-income',
      irsLinks: ['1099-nec', 'schedule-c'],
      related: ['taxes-101/w9-and-1099', 'taxes-101/schedule-c-basics', 'deductions/home-office-and-other'],
      body: {
        en: {
          title: "What counts as income",
          summary: "Fees and tips are income; the virtual card, your own transfers, and refunds are not.",
          sections: [
            {
              heading: "Income: the short list",
              bullets: [
                `The ${FEE} delivery fee on every completed delivery. You keep 100% of it.`,
                "Every tip, whether it came through the app or a customer handed you cash. 100% is yours, and 100% is taxable.",
                "Income is counted when it's paid to you, which for most Runners means the weekly Stripe Connect payout.",
              ],
            },
            {
              heading: "Not income",
              bullets: [
                "Groceries paid for with the Grocery List Plus-funded virtual card at the store. You never spent your own money, so there's nothing to report.",
                "Money you move from your business account to your personal account. That's you paying yourself, not new income.",
                "A refund on gear you returned.",
              ],
            },
            {
              heading: "Gross vs. net",
              paragraphs: [
                `Your pay statement shows the gross (fees plus tips), the lane pass line (${LANE} per delivery, capped at ${LANE_MAX} a week), and the net that hit your bank. Report the gross as income and the lane pass as an expense. Don't report the net as income; you'd be skipping a deduction you're entitled to.`,
                "Cash tips won't be on any statement or 1099-NEC. Write them down the same day. They're still income.",
              ],
            },
            {
              heading: "Timing",
              paragraphs: [
                `A delivery becomes payout-eligible when your customer confirms receipt or after ${CONFIRM_H} hours automatically, then pays out on the weekly cycle. For taxes, count the year the money was paid to you. A December delivery paid in January is next year's income. Confirm with an accountant if you have a large week near the year boundary.`,
              ],
            },
          ],
          actions: [
            "Add a 'cash tips' note to your phone and log them daily.",
            "Compare last week's pay statement gross to what landed in your bank; the difference should be the lane pass.",
            "Total your year-to-date gross so your next estimated payment isn't a guess.",
          ],
        },
        es: {
          title: "Qué cuenta como ingreso",
          summary: "Las tarifas y propinas son ingreso; la tarjeta virtual, tus propias transferencias y los reembolsos no lo son.",
          sections: [
            {
              heading: "Ingreso: la lista corta",
              bullets: [
                `La tarifa de entrega de ${FEE} por cada entrega completada. Te quedas con el 100%.`,
                "Cada propina, ya sea que llegue por la app o que un cliente te dé efectivo. El 100% es tuyo, y el 100% es gravable.",
                "El ingreso se cuenta cuando te lo pagan, lo que para la mayoría de los Runners significa el pago semanal por Stripe Connect.",
              ],
            },
            {
              heading: "No es ingreso",
              bullets: [
                "La despensa pagada con la tarjeta virtual financiada por Lista de Compras Plus en la tienda. Nunca gastaste tu propio dinero, así que no hay nada que reportar.",
                "El dinero que mueves de tu cuenta de negocio a tu cuenta personal. Eso es pagarte a ti mismo, no un ingreso nuevo.",
                "Un reembolso por equipo que devolviste.",
              ],
            },
            {
              heading: "Bruto vs. neto",
              paragraphs: [
                `Tu estado de pago muestra el bruto (tarifas más propinas), la línea del lane pass (${LANE} por entrega, con tope de ${LANE_MAX} a la semana) y el neto que llegó a tu banco. Reporta el bruto como ingreso y el lane pass como gasto. No reportes el neto como ingreso; estarías dejando pasar una deducción a la que tienes derecho.`,
                "Las propinas en efectivo no aparecerán en ningún estado ni en el 1099-NEC. Anótalas el mismo día. Siguen siendo ingreso.",
              ],
            },
            {
              heading: "El momento",
              paragraphs: [
                `Una entrega queda lista para pago cuando tu cliente confirma la recepción o automáticamente después de ${CONFIRM_H} horas, y luego se paga en el ciclo semanal. Para impuestos, cuenta el año en que te pagaron el dinero. Una entrega de diciembre pagada en enero es ingreso del año siguiente. Confírmalo con un contador si tienes una semana grande cerca del cambio de año.`,
              ],
            },
          ],
          actions: [
            "Agrega una nota de 'propinas en efectivo' en tu teléfono y regístralas a diario.",
            "Compara el bruto del estado de pago de la semana pasada con lo que llegó a tu banco; la diferencia debería ser el lane pass.",
            "Suma tu bruto del año hasta la fecha para que tu próximo pago estimado no sea una adivinanza.",
          ],
        },
      },
    },
    {
      slug: 'tax-calendar',
      irsLinks: ['1040-ES', 'schedule-c', 'schedule-se', '1099-nec'],
      related: ['taxes-101/quarterly-estimated-taxes', 'recordkeeping-and-money/mileage-log-habit', 'recordkeeping-and-money/receipts-and-bookkeeping'],
      body: {
        en: {
          title: "A Runner's tax calendar",
          summary: "Four estimated payments, one annual return, and a few habits that keep each one from being a scramble.",
          sections: [
            {
              heading: "Every payout (weekly)",
              bullets: [
                `Move ${SET_ASIDE} of that week's profit into your tax savings account.`,
                "Check the pay statement: gross, lane pass line, net.",
                "Log any cash tips you received.",
              ],
            },
            {
              heading: "Every month",
              bullets: [
                "Reconcile your mileage log against your delivery history.",
                "Photograph and file receipts: bags, mounts, phone bill, tolls, parking.",
                "Glance at your business account. Anything you can't explain, note it now while you remember.",
              ],
            },
            {
              heading: "Four times a year",
              bullets: [
                "Mid-April: estimated payment for January to March.",
                "Mid-June: estimated payment for April and May.",
                "Mid-September: estimated payment for June to August.",
                "Mid-January: estimated payment for September to December of the prior year.",
                "Use Form 1040-ES. Check your state's schedule too.",
              ],
            },
            {
              heading: "Once a year",
              bullets: [
                "Late January: watch for a 1099-NEC from Grocery List Plus if your entity type gets one and you're above the IRS reporting threshold.",
                "February: total your mileage, expenses, and gross income. Give the packet to your accountant or tax software.",
                "Mid-April: file Form 1040 with Schedule C and Schedule SE. Your first quarterly payment for the new year is due the same day.",
                "Any time your entity type or name changes: contact support so we can collect a new W-9.",
              ],
            },
            {
              heading: "The one habit that makes the rest easy",
              paragraphs: [
                "Two minutes at the end of each shift: confirm the day's miles are logged and today's receipts are photographed. Everything above gets easier when that's true.",
              ],
            },
          ],
          actions: [
            "Put all four estimated dates and the April filing date in your calendar now.",
            "Set a monthly 'receipts and miles' reminder for the first Sunday.",
            "Decide whether you're using an accountant or software this year, and book or buy it before the busy season.",
          ],
        },
        es: {
          title: "El calendario fiscal de un Runner",
          summary: "Cuatro pagos estimados, una declaración anual y unos cuantos hábitos que evitan que cada uno sea una carrera.",
          sections: [
            {
              heading: "En cada pago (semanal)",
              bullets: [
                `Pasa el ${SET_ASIDE} de la ganancia de esa semana a tu cuenta de ahorros para impuestos.`,
                "Revisa el estado de pago: bruto, línea del lane pass, neto.",
                "Registra cualquier propina en efectivo que hayas recibido.",
              ],
            },
            {
              heading: "Cada mes",
              bullets: [
                "Cuadra tu registro de millaje con tu historial de entregas.",
                "Fotografía y archiva recibos: bolsas, soportes, factura del teléfono, peajes, estacionamiento.",
                "Échale un vistazo a tu cuenta de negocio. Lo que no puedas explicar, anótalo ahora que lo recuerdas.",
              ],
            },
            {
              heading: "Cuatro veces al año",
              bullets: [
                "Mediados de abril: pago estimado por enero a marzo.",
                "Mediados de junio: pago estimado por abril y mayo.",
                "Mediados de septiembre: pago estimado por junio a agosto.",
                "Mediados de enero: pago estimado por septiembre a diciembre del año anterior.",
                "Usa el Formulario 1040-ES. Revisa también el calendario de tu estado.",
              ],
            },
            {
              heading: "Una vez al año",
              bullets: [
                "Finales de enero: espera un 1099-NEC de Lista de Compras Plus si tu tipo de entidad recibe uno y superaste el umbral de reporte del IRS.",
                "Febrero: suma tu millaje, tus gastos y tu ingreso bruto. Entrega el paquete a tu contador o a tu software de impuestos.",
                "Mediados de abril: presenta el Formulario 1040 con el Anexo C y el Anexo SE. Tu primer pago trimestral del año nuevo vence el mismo día.",
                "Cada vez que cambie tu tipo de entidad o tu nombre: entrega un W-9 nuevo a Lista de Compras Plus.",
              ],
            },
            {
              heading: "El único hábito que facilita todo lo demás",
              paragraphs: [
                "Dos minutos al final de cada turno: confirma que las millas del día estén registradas y los recibos de hoy fotografiados. Todo lo de arriba se vuelve más fácil cuando eso se cumple.",
              ],
            },
          ],
          actions: [
            "Pon las cuatro fechas de pagos estimados y la fecha de presentación de abril en tu calendario ahora.",
            "Configura un recordatorio mensual de 'recibos y millas' para el primer domingo.",
            "Decide si este año usarás contador o software, y agéndalo o cómpralo antes de la temporada alta.",
          ],
        },
      },
    },
  ],
};
